import type { ArtworkFieldOption } from './digitalProofingArtworkTypes'
import type {
  CustomerAddressFieldValue,
  DynamicArtworkField,
  DynamicFieldErrors,
  DynamicFieldValue,
  DynamicFieldValues,
  DynamicProofingFormValidation,
  SelectFieldValue
} from './dynamicProofingFormTypes'
import validator from 'validator'

export const blankSelectFieldValue: SelectFieldValue = { value: '-1', label: 'Please Select' }

const initialValueMap: Record<string, DynamicFieldValue> = {
  text: '',
  date: '',
  phone: '',
  select: blankSelectFieldValue,
  number: ''
}

const comparisonOperators = {
  '===': (a: DynamicFieldValue, b: DynamicFieldValue) => a === b,
  // Preserve the legacy component's native JS comparison behavior for string, number, and date values.
  '<=': (a: DynamicFieldValue, b: DynamicFieldValue) => (a as number) <= (b as number),
  '>=': (a: DynamicFieldValue, b: DynamicFieldValue) => (a as number) >= (b as number),
  '<': (a: DynamicFieldValue, b: DynamicFieldValue) => (a as number) < (b as number),
  '>': (a: DynamicFieldValue, b: DynamicFieldValue) => (a as number) > (b as number)
}

const comparisonOperatorLabels = {
  '===': 'equal to',
  '<=': 'less than or equal to',
  '>=': 'greater than or equal to',
  '<': 'less than',
  '>': 'greater than'
}

type ComparisonOperator = keyof typeof comparisonOperators

const isSelectFieldValue = (value: DynamicFieldValue): value is SelectFieldValue =>
  typeof value === 'object' && value !== null && 'value' in value

const isCustomerAddressFieldValue = (value: DynamicFieldValue): value is CustomerAddressFieldValue =>
  typeof value === 'object' && value !== null && 'customerAddressId' in value

const fieldValueForComparison = (value: DynamicFieldValue) => (isSelectFieldValue(value) ? value.value : value)

const isBlankComparisonValue = (value: DynamicFieldValue) => {
  const comparisonValue = fieldValueForComparison(value)
  return (!comparisonValue && comparisonValue !== 0) || comparisonValue === '-1'
}

export const hasFieldValue = (value: DynamicFieldValue) =>
  isSelectFieldValue(value)
    ? value.value !== '-1'
    : value !== '' && value !== '-1' && value !== null && value !== undefined

export const sortAndFilterArtworkFields = (fields: DynamicArtworkField[], showCouponFields: boolean) => {
  const seenNames = new Set<string>()

  return [...fields]
    .sort((a, b) => Number(a.form_sort_order ?? 0) - Number(b.form_sort_order ?? 0) || Number(a.id) - Number(b.id))
    .filter(field => {
      if (seenNames.has(field.name)) return false
      seenNames.add(field.name)
      return true
    })
    .filter(field => field.is_active && !field.is_hidden)
    .filter(field => !field.requires_coupon || showCouponFields)
}

export const normalizedArtworkFieldOptions = (field: DynamicArtworkField) =>
  [...(field.artwork_field_options ?? [])]
    .filter(option => option.is_active !== false)
    .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0) || Number(a.id) - Number(b.id))

export const selectFieldValueFromOption = (option: ArtworkFieldOption): SelectFieldValue => ({
  value: option.value,
  label: option.label
})

export const initialValueForField = (field: DynamicArtworkField): DynamicFieldValue => {
  if (field.html_input_type === 'date' && field.min_value) {
    const minDate = new Date()
    minDate.setDate(minDate.getDate() + field.min_value)
    return minDate
  }

  if (field.is_address_book) return null

  if (field.html_input_type === 'select') {
    const defaultOption = normalizedArtworkFieldOptions(field).find(option => option.is_default)
    return defaultOption ? selectFieldValueFromOption(defaultOption) : blankSelectFieldValue
  }

  return initialValueMap[field.html_input_type] ?? ''
}

export const hiddenValueForField = (field: DynamicArtworkField): DynamicFieldValue => {
  if (field.is_address_book) return null
  if (field.html_input_type === 'select') return blankSelectFieldValue

  return initialValueMap[field.html_input_type] ?? ''
}

export const buildInitialValues = (fields: DynamicArtworkField[]): DynamicFieldValues =>
  fields.reduce<DynamicFieldValues>((initialValues, field) => {
    initialValues[field.name] = initialValueForField(field)

    return initialValues
  }, {})

const dynamicFieldValueEquals = (left: DynamicFieldValue, right: DynamicFieldValue) => {
  if (left instanceof Date || right instanceof Date) {
    return left instanceof Date && right instanceof Date && left.getTime() === right.getTime()
  }

  if (typeof left === 'object' || typeof right === 'object') {
    return JSON.stringify(left) === JSON.stringify(right)
  }

  return left === right
}

export const dynamicFieldValuesEqual = (left: DynamicFieldValues, right: DynamicFieldValues) => {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])
  return [...keys].every(key => dynamicFieldValueEquals(left[key], right[key]))
}

export const sanitizeDynamicFieldValues = (
  fields: DynamicArtworkField[],
  visibleFields: DynamicArtworkField[],
  currentValues: DynamicFieldValues
) => {
  const visibleFieldNames = new Set(visibleFields.map(field => field.name))
  const visibleOptionsByFieldName = visibleFields.reduce<Record<string, ArtworkFieldOption[]>>(
    (optionsByFieldName, field) => {
      optionsByFieldName[field.name] = normalizedArtworkFieldOptions(field)
      return optionsByFieldName
    },
    {}
  )

  return fields.reduce<DynamicFieldValues>((nextValues, field) => {
    if (!visibleFieldNames.has(field.name)) {
      nextValues[field.name] = hiddenValueForField(field)
      return nextValues
    }

    const currentValue = currentValues[field.name]
    if (field.is_address_book) {
      nextValues[field.name] = currentValue ?? null
      return nextValues
    }

    if (field.html_input_type !== 'select') {
      nextValues[field.name] = currentValue ?? initialValueForField(field)
      return nextValues
    }

    const visibleOptions = visibleOptionsByFieldName[field.name] ?? []
    const selectedValue = isSelectFieldValue(currentValue) ? currentValue.value : undefined
    const selectedOption = visibleOptions.find(option => option.value === selectedValue)
    const defaultOption = visibleOptions.find(option => option.is_default)

    // Hidden or unavailable select options are cleared before validation and preview submission.
    nextValues[field.name] = selectedOption
      ? selectFieldValueFromOption(selectedOption)
      : defaultOption
        ? selectFieldValueFromOption(defaultOption)
        : blankSelectFieldValue

    return nextValues
  }, {})
}

export const validateUrl = (value: string) => {
  if (validator.isURL(value)) {
    return { valid: true, message: null }
  }

  return { valid: false, message: `The URL ${value} is not a valid one.` }
}

export const validateDynamicProofingForm = (
  fields: DynamicArtworkField[],
  values: DynamicFieldValues,
  showNoFields?: boolean | null
): DynamicProofingFormValidation => {
  const fieldErrors: DynamicFieldErrors = {}
  let comparisonToastMessage: string | undefined

  fields
    .filter(field => field.comparison_field_name)
    .forEach(field => {
      const comparisonFieldName = field.comparison_field_name
      if (!comparisonFieldName) return

      const comparisonField = fields.find(candidate => candidate.name === comparisonFieldName)
      const thisValue = values[field.name]
      const comparisonValue = values[comparisonFieldName]

      if (isBlankComparisonValue(comparisonValue) || isBlankComparisonValue(thisValue)) return

      const operator = field.comparison_operator as ComparisonOperator
      const comparisonFn = comparisonOperators[operator]
      if (!comparisonFn) return

      const normalizedThisValue = fieldValueForComparison(thisValue)
      const normalizedComparisonValue = fieldValueForComparison(comparisonValue)

      if (!comparisonFn(normalizedThisValue, normalizedComparisonValue)) {
        const operatorLabel = comparisonOperatorLabels[operator]
        const comparisonLabel = comparisonField?.html_label ?? comparisonFieldName
        const message = `${field.html_label} must be ${operatorLabel} ${comparisonLabel}`

        fieldErrors[field.name] = { message, showInErrorSummary: true }
        comparisonToastMessage = `"${field.html_label}" must be ${operatorLabel} "${comparisonLabel}"`
      }
    })

  const fieldsValid = fields.every(field => {
    const value = values[field.name]

    if (field.is_url && typeof value === 'string' && value.length > 0) {
      const validation = validateUrl(value)
      if (!validation.valid) {
        fieldErrors[field.name] = {
          message: validation.message || '',
          showInErrorSummary: false
        }
        return false
      }
    }

    if (field.is_required) {
      return hasFieldValue(value)
    }

    return true
  })

  const isFormValid = !!showNoFields || (fieldsValid && Object.keys(fieldErrors).length === 0)
  return { isFormValid, fieldErrors, comparisonToastMessage }
}

export const normalizeFieldValueForSubmission = (value: DynamicFieldValue) => {
  if (isCustomerAddressFieldValue(value)) {
    const { customerAddressId, ...rest } = value
    return {
      ...rest,
      ...(customerAddressId !== undefined && customerAddressId !== null && { customer_address_id: customerAddressId })
    }
  }

  if (isSelectFieldValue(value)) {
    return { value: value.value, label: value.label }
  }

  return value
}

export const buildDynamicProofingFormData = (fields: DynamicArtworkField[], values: DynamicFieldValues) => {
  const formData = new FormData()
  const formValues = fields.reduce<Record<string, unknown>>((serializedValues, field) => {
    serializedValues[field.name] = normalizeFieldValueForSubmission(values[field.name])
    return serializedValues
  }, {})

  formData.append('formValues', JSON.stringify(formValues))
  return formData
}
